import { UcoastEl } from '@/scripts/core/UcoastEl'
import { FEATURED_PRODUCT_VARIANT_CHANGE_EVENT } from '@/scripts/theme/constants'
import { type ProductForm } from '@/scripts/product/product-form'
import type { ProductVariant } from '@/scripts/shopify'
import {
	beginStandardProductSelection,
	resolveStandardProductSelection,
} from '@/scripts/platform/standard-events'
import { replaceProductUrlParameters } from '@/scripts/product/product-url'
import { motionEnabled } from '@/scripts/core/motion'
import { decodePoster } from '@/scripts/core/media-poster'

export class HeroFeaturedProduct extends UcoastEl {
	static htmlSelector = 'hero-featured-product'
	private readonly variantTransitionDuration = 300
	private readonly mediaObscureDuration = 180

	private productForm: HTMLFormElement | null = null
	private ATCButton: HTMLButtonElement | null = null
	private variants: NodeListOf<HTMLElement>
	private variantPannel: HTMLElement | null
	private heroMedia: HTMLElement | null
	private featuredMedia: HTMLElement | null
	private defaultFeaturedMedia: Node[]
	private selectShadeButton: HTMLButtonElement | null = null
	private shadeLabel: HTMLElement | null = null
	private shadeDescription: HTMLElement | null = null
	private panelRequest?: AbortController
	private shadePromptObserver?: MutationObserver
	private heroCopyObserver?: ResizeObserver
	private mediaPaused = false
	private panelTimeout?: number
	private pendingIndicatorTimeout?: number
	private transitionFinishTimeout?: number
	private transitionStartedAt?: number
	private pendingVariantId?: string
	private requiresShade: boolean
	private hasSelectedShade: boolean
	private variantData: ProductVariant[]
	private featuredMediaRequest = 0
	private featuredMediaAnimation?: Animation
	private featuredMediaTransition?: HTMLElement
	private variantChangeFromKeyboard = false
	private prefetchedMedia?: {
		variantId: string
		fragment: DocumentFragment
		ready: Promise<boolean>
	}

	constructor() {
		super()

		this.productForm = this.querySelector<HTMLFormElement>('form') ?? null
		this.selectShadeButton = this.querySelector<HTMLButtonElement>('.select-shade__button') ?? null
		this.shadeLabel = this.querySelector<HTMLElement>('[data-selected-shade-label]') ?? null
		this.shadeDescription = this.querySelector<HTMLElement>('[data-selected-shade-description]') ?? null
		this.requiresShade = this.hasAttribute('data-requires-shade')
		this.hasSelectedShade = this.hasAttribute('data-has-selected-shade')
		const variantData = this.querySelector<HTMLScriptElement>(
			'[data-uc-variant-data]'
		)?.textContent
		this.variantData = variantData ? JSON.parse(variantData) : []

		this.ATCButton =
			this.productForm?.querySelector<HTMLButtonElement>(
				'.product-form__submit'
			) ?? null

		this.variants = this.querySelectorAll<HTMLElement>(
			'.hero__featured-product__variant'
		)

		const product = this.closest<HTMLElement>('.product')

		this.variantPannel = product?.querySelector<HTMLElement>('.product-media__panels') ?? null
		this.heroMedia = product?.querySelector<HTMLElement>('[data-product-hero-media]') ?? null
		this.observeHeroCopy()
		this.featuredMedia = this.querySelector<HTMLElement>('[data-featured-product-media]') ?? null
		this.defaultFeaturedMedia = Array.from(
			this.featuredMedia?.childNodes ?? [],
			(node) => node.cloneNode(true)
		)


		if (!this.productForm || !this.ATCButton) {
			return
		}

		if (this.variants.length > 0 && !this.hasSelectedShade) {
			this.ATCButton.disabled = true
		}

		if (this.requiresShade && !this.hasSelectedShade) {
			this.ATCButton.type = 'button'
			this.ATCButton.addEventListener('click', this.handleShadePrompt)
			this.syncShadeDependentControls()
			const productInfo = this.closest('product-info')
			this.syncShadePrompt()
			if (productInfo) {
				this.shadePromptObserver = new MutationObserver(this.syncShadePrompt)
				this.shadePromptObserver.observe(productInfo, {
					attributes: true,
					attributeFilter: ['class'],
				})
			}
		}

		this.setupVariantListeners()

		if (this.selectShadeButton) {
			if (!this.requiresShade) this.ATCButton.disabled = false
			this.selectShadeButton.addEventListener('click', () => {
				this.classList.remove('has-shade')
			})
		}
	}

	override disconnectedCallback(): void {
		this.shadePromptObserver?.disconnect()
		this.heroCopyObserver?.disconnect()
		this.featuredMediaRequest++
		this.featuredMediaAnimation?.cancel()
		this.featuredMediaTransition?.remove()
		this.prefetchedMedia = undefined
		this.ATCButton?.removeEventListener('click', this.handleShadePrompt)
		this.panelRequest?.abort()
		window.clearTimeout(this.panelTimeout)
		window.clearTimeout(this.pendingIndicatorTimeout)
		window.clearTimeout(this.transitionFinishTimeout)
		this.completeVariantTransition()
		super.disconnectedCallback()
	}

	private syncShadePrompt = (): void => {
		if (this.hasSelectedShade || !this.ATCButton) return
		const expanded =
			this.closest('product-info')?.classList.contains('expanded') ?? false
		this.ATCButton.disabled = expanded
		if (expanded) this.ATCButton.setAttribute('aria-disabled', 'true')
		else this.ATCButton.removeAttribute('aria-disabled')
	}

	private handleShadePrompt = (event: MouseEvent): void => {
		if (this.hasSelectedShade) return

		event.preventDefault()
		this.closest('product-info')?.dispatchEvent(
			new CustomEvent('product-info:expand')
		)
	}

	private setupVariantListeners(): void {
		this.variants.forEach((variant) => {
			const input = variant.querySelector<HTMLInputElement>('input')

			if (!input) {
				return
			}

			input.addEventListener('change', () => {
				if (input.checked) {
					this.selectVariant(input, this.variantChangeFromKeyboard)
					this.variantChangeFromKeyboard = false
				}
			})
			input.addEventListener('keydown', () => {
				this.variantChangeFromKeyboard = true
			})
			variant.addEventListener('pointerdown', () => {
				this.variantChangeFromKeyboard = false
			})
			if (this.hasAttribute('data-stage-featured-media')) {
				const prefetch = (): void => {
					if (!this.prefetchedMedia && !input.checked) {
						this.prefetchedMedia = this.prepareFeaturedMedia(input.value, 'low')
					}
				}
				variant.addEventListener('pointerenter', prefetch)
				input.addEventListener('focus', prefetch)
			}
		})
	}

	private selectVariant(input: HTMLInputElement, fromKeyboard = false): void {
		if (!this.productForm || !this.ATCButton) {
			return
		}
		const variantId = input.value

		const variantInput =
			this.productForm.querySelector<HTMLInputElement>(
				'[data-uc-product-variant-id]'
			)

		if (!variantInput) {
			return
		}

		const variant = this.variantData.find(
			(candidate) => `${candidate.id}` === variantId
		)
		const standardSelection = beginStandardProductSelection(
			this,
			variant?.options ?? []
		)
		this.beginVariantTransition(input)

		variantInput.value = variantId

		this.hasSelectedShade = true
		this.setAttribute('data-has-selected-shade', '')
		this.syncShadeDependentControls()
		this.syncShadeCopy(input, variant)
		this.ATCButton.disabled = false
		this.ATCButton.type = 'submit'
		this.updateCommerceState(input)
		this.updateProductUrl(variantId)
		this.renderFeaturedMedia(variantId, !fromKeyboard)

		this.variants.forEach((variant) => {
			const input = variant.querySelector<HTMLInputElement>('input')

			if (input) {
				input.checked = input.value === variantId
				variant.classList.toggle(
					'is-selected',
					input.value === variantId
				)
			}
		})

		this.closest('section')
			?.querySelector<ProductForm>('sticky-atc product-form')
			?.syncStickyFormFields()

		this.closest('.featured-product-carousel')?.dispatchEvent(
			new CustomEvent(FEATURED_PRODUCT_VARIANT_CHANGE_EVENT, {
				detail: { variantId },
			})
		)

		void this.renderVariantPanel(variantId)
		resolveStandardProductSelection(standardSelection, variant)
	}

	private syncShadeCopy(input: HTMLInputElement, variant?: ProductVariant): void {
		const label = input.dataset.shadeLabel?.trim() || variant?.title
		if (this.shadeLabel && label) this.shadeLabel.textContent = label
		if (!this.shadeDescription) return
		const description = input.dataset.shadeDescription?.trim() ?? ''
		this.shadeDescription.textContent = description
		this.shadeDescription.hidden = description === ''
	}

	private beginVariantTransition(input: HTMLInputElement): void {
		window.clearTimeout(this.pendingIndicatorTimeout)
		window.clearTimeout(this.transitionFinishTimeout)
		this.transitionFinishTimeout = undefined
		this.transitionStartedAt = performance.now()
		this.pendingVariantId = input.value
		this.setAttribute('aria-busy', 'true')
		this.closest('section')?.setAttribute('data-variant-transition', 'loading')
		this.variants.forEach((variant) => {
			const candidate = variant.querySelector<HTMLInputElement>('input')
			const pending = candidate === input
			variant.classList.toggle('is-pending', pending)
			variant.classList.toggle('is-dark', pending && this.shadeIsDark(input.dataset.shadeColor))
			variant.classList.remove('is-loading')
			variant.toggleAttribute('aria-busy', pending)
		})
		this.pendingIndicatorTimeout = window.setTimeout(() => {
			if (this.pendingVariantId !== input.value) return
			input.closest('.hero__featured-product__variant')?.classList.add('is-loading')
		}, 180)
	}

	private shadeIsDark(color?: string): boolean {
		if (!color) return false
		const hex = color.trim().match(/^#([\da-f]{3}|[\da-f]{6})$/i)?.[1]
		if (!hex) return false
		const channels = (
			hex.length === 3
				? hex.split('').map((value) => value + value)
				: hex.match(/.{2}/g) ?? []
		).map((value) => Number.parseInt(value, 16) / 255)
		if (channels.length !== 3 || channels.some(Number.isNaN)) return false
		const [red, green, blue] = channels.map((value) =>
			value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
		)
		return 0.2126 * red + 0.7152 * green + 0.0722 * blue < 0.35
	}

	private transitionMotionEnabled(): boolean {
		return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
	}

	private clearPendingIndicator(variantId = this.pendingVariantId): void {
		if (variantId && this.pendingVariantId !== variantId) return
		window.clearTimeout(this.pendingIndicatorTimeout)
		this.pendingIndicatorTimeout = undefined
		this.variants.forEach((variant) => variant.classList.remove('is-loading'))
	}

	private finishVariantTransition(variantId = this.pendingVariantId): void {
		if (variantId && this.pendingVariantId !== variantId) return
		this.clearPendingIndicator(variantId)
		const elapsed = this.transitionStartedAt === undefined
			? this.variantTransitionDuration
			: performance.now() - this.transitionStartedAt
		const remaining = this.transitionMotionEnabled()
			? Math.max(0, this.variantTransitionDuration - elapsed)
			: 0
		if (remaining > 0) {
			window.clearTimeout(this.transitionFinishTimeout)
			this.transitionFinishTimeout = window.setTimeout(
				() => this.completeVariantTransition(variantId),
				remaining
			)
			return
		}
		this.completeVariantTransition(variantId)
	}

	private completeVariantTransition(variantId = this.pendingVariantId): void {
		if (variantId && this.pendingVariantId !== variantId) return
		window.clearTimeout(this.transitionFinishTimeout)
		this.transitionFinishTimeout = undefined
		this.transitionStartedAt = undefined
		this.pendingVariantId = undefined
		this.removeAttribute('aria-busy')
		this.closest('section')?.removeAttribute('data-variant-transition')
		this.variants.forEach((variant) => {
			variant.classList.remove('is-pending', 'is-loading', 'is-dark')
			variant.removeAttribute('aria-busy')
		})
	}

	private async waitForMediaObscureFloor(signal: AbortSignal): Promise<void> {
		if (!this.transitionMotionEnabled() || this.transitionStartedAt === undefined) return
		const remaining = Math.max(
			0,
			this.mediaObscureDuration - (performance.now() - this.transitionStartedAt)
		)
		if (remaining === 0) return
		await new Promise<void>((resolve, reject) => {
			const done = () => {
				signal.removeEventListener('abort', abort)
				resolve()
			}
			const timeout = window.setTimeout(done, remaining)
			const abort = () => {
				window.clearTimeout(timeout)
				signal.removeEventListener('abort', abort)
				reject(new DOMException('Media selection aborted', 'AbortError'))
			}
			if (signal.aborted) abort()
			else signal.addEventListener('abort', abort, { once: true })
		})
	}

	private updateProductUrl(variantId: string): void {
		if (!this.dataset.productUrl || this.dataset.updateUrl === 'false') return
		replaceProductUrlParameters(
			{ variant: variantId },
			this.dataset.productUrl
		)
	}

	private renderFeaturedMedia(variantId: string, animate: boolean): void {
		if (!this.featuredMedia) return
		if (this.hasAttribute('data-stage-featured-media')) {
			void this.renderReadyFeaturedMedia(variantId, animate)
			return
		}
		const template = Array.from(
			this.querySelectorAll<HTMLTemplateElement>('[data-variant-media-template]')
		).find((candidate) => candidate.dataset.variantMediaTemplate === variantId)
		const media = template
			? Array.from(template.content.childNodes, (node) => node.cloneNode(true))
			: this.defaultFeaturedMedia.map((node) => node.cloneNode(true))

		this.featuredMedia.replaceChildren(...media)
	}

	private prepareFeaturedMedia(variantId: string, priority: 'high' | 'low') {
		const template = Array.from(
			this.querySelectorAll<HTMLTemplateElement>('[data-variant-media-template]')
		).find((candidate) => candidate.dataset.variantMediaTemplate === variantId)
		if (!template) return undefined

		const fragment = document.importNode(template.content, true)
		const images = Array.from(fragment.querySelectorAll('img'))
		const ready = Promise.all(images.map(async (image) => {
			image.loading = 'eager'
			image.fetchPriority = priority
			await image.decode()
			return image.naturalWidth > 0
		})).then((results) => results.length > 0 && results.every(Boolean), () => false)
		return { variantId, fragment, ready }
	}

	private async renderReadyFeaturedMedia(variantId: string, animate: boolean): Promise<void> {
		const target = this.featuredMedia
		if (!target) return
		const request = ++this.featuredMediaRequest
		this.featuredMediaAnimation?.cancel()
		this.featuredMediaAnimation = undefined
		this.featuredMediaTransition?.remove()
		this.featuredMediaTransition = undefined
		const staged = this.prefetchedMedia?.variantId === variantId
			? this.prefetchedMedia
			: this.prepareFeaturedMedia(variantId, 'high')
		this.prefetchedMedia = undefined
		staged?.fragment.querySelectorAll('img').forEach((image) => {
			image.fetchPriority = 'high'
		})
		if (!staged || !await staged.ready) return
		if (request !== this.featuredMediaRequest || !this.isConnected) return
		if (!animate || !motionEnabled() || target.childElementCount === 0) {
			target.replaceChildren(staged.fragment)
			return
		}

		const transition = document.createElement('div')
		transition.className = 'hero__featured-product__media-transition'
		transition.setAttribute('aria-hidden', 'true')
		transition.append(staged.fragment)
		this.featuredMediaTransition = transition
		target.append(transition)
		this.featuredMediaAnimation = transition.animate(
			[{ opacity: 0 }, { opacity: 1 }],
			{
				duration: 220,
				easing: 'cubic-bezier(0.19, 1, 0.22, 1)',
				fill: 'forwards',
			}
		)
		await this.featuredMediaAnimation.finished.catch(() => undefined)
		if (request !== this.featuredMediaRequest || !this.isConnected) {
			transition.remove()
			return
		}
		const nextMedia = Array.from(transition.childNodes)
		target.replaceChildren(...nextMedia)
		this.featuredMediaAnimation = undefined
		this.featuredMediaTransition = undefined
	}

	private updateCommerceState(input: HTMLInputElement): void {
		if (!this.ATCButton) return
		const inventoryStatus = input.dataset.inventoryStatus
		const waitlist =
			inventoryStatus === 'ComingSoon' || inventoryStatus === 'OutOfStock'
		const available = input.dataset.variantAvailable === 'true'
		const inventoryStatusText = this.ATCButton.querySelector<HTMLElement>(
			'[data-uc-inventory-status-text]'
		)

		this.ATCButton.disabled = !available && !waitlist
		this.ATCButton.toggleAttribute('aria-disabled', !available && !waitlist)
		if (waitlist) {
			this.ATCButton.dataset.oosPopupTrigger = input.value
			this.ATCButton.dataset.productTitle = this.dataset.productTitle ?? ''
		} else {
			delete this.ATCButton.dataset.oosPopupTrigger
			delete this.ATCButton.dataset.productTitle
		}
		if (inventoryStatusText && input.dataset.inventoryActionText) {
			inventoryStatusText.textContent = input.dataset.inventoryActionText
		}
	}

	private captureMediaPauseIntent(): void {
		const videos = [this.heroMedia, this.variantPannel].flatMap((region) =>
			Array.from(
				region?.querySelectorAll<
					HTMLElement & {
						userPaused?: boolean
						userPlayRequested?: boolean
					}
				>('ucoast-video') ?? []
			)
		)
		if (videos.some((video) => video.userPaused)) this.mediaPaused = true
		else if (videos.some((video) => video.userPlayRequested))
			this.mediaPaused = false
	}

	private showSelectedMediaFallback(variantId: string, regionIndex: number): void {
		this.captureMediaPauseIntent()
		const input = Array.from(this.variants)
			.flatMap((el) => Array.from(el.querySelectorAll('input')))
			.find((el) => el.value === variantId)
		const copy = this.closest('.product')?.querySelector<HTMLTemplateElement>(
			`template[data-selected-media-copy="${variantId}"]`
		)
		const region = [this.heroMedia, this.variantPannel][regionIndex]
		if (!region) return
		const height = region.getBoundingClientRect().height
		if (height) region.style.minHeight = `${height}px`
		region.style.backgroundColor = input?.dataset.shadeColor ?? ''
		region.dataset.mediaState = 'failed'
		region.dataset.selectedMediaVariant = variantId
		region.toggleAttribute('data-media-paused', this.mediaPaused)
		const nextCopy = copy?.content.querySelector(
			`[data-media-copy-region="${regionIndex}"]`
		)
		if (regionIndex === 0) {
			const nextHeroPosition = nextCopy?.querySelector<HTMLTemplateElement>(
				'template[data-pdp-hero-copy-position]'
			)?.dataset.pdpHeroCopyPosition
			if (nextHeroPosition)
				region.dataset.pdpHeroContentPosition = nextHeroPosition
		}
		const fallback = nextCopy?.cloneNode(true) as HTMLElement | undefined
		fallback?.querySelectorAll('picture, img, template[data-deferred-media]')
			.forEach((media) => media.remove())
		fallback?.querySelectorAll('art-direction').forEach((media) => {
			media.removeAttribute('data-media-deferred')
		})
		fallback?.querySelectorAll<HTMLElement>('.product-media__panel')
			.forEach((panel) => { panel.style.backgroundColor = 'inherit' })
		region.replaceChildren(...Array.from(fallback?.childNodes ?? [],
			(node) => document.importNode(node, true)))
		region
			.querySelector('template[data-pdp-hero-copy-position]')
			?.remove()
		this.bindLoadingToggle(region)
		this.appendMediaRetry(region, variantId)
	}

	private appendMediaRetry(region: HTMLElement, variantId: string): void {
		const retry = document.createElement('button')
		retry.type = 'button'
		retry.className = 'button media-retry'
		retry.textContent = this.dataset.mediaRetryLabel ?? ''
		retry.addEventListener(
			'click',
			() => {
				this.querySelector<HTMLInputElement>('input:checked')?.focus({
					preventScroll: true,
				})
				void this.renderVariantPanel(variantId)
			},
			{ once: true }
		)
		region.append(retry)
	}

	private async prepareRegionMedia(
		region: HTMLElement,
		signal: AbortSignal,
		priority: 'high' | 'auto'
	): Promise<void> {
		for (const template of region.querySelectorAll<HTMLTemplateElement>('template[data-deferred-media]')) {
			for (const poster of template.content.querySelectorAll('picture, img.no-picture')) {
				template.before(document.importNode(poster, true))
				poster.remove()
			}
		}
		let abort: () => void = () => {}
		try {
			await Promise.race([
				Promise.all(Array.from(region.querySelectorAll('img'), async (image) => {
					image.loading = 'eager'
					image.fetchPriority = priority
					if (!await decodePoster(image)) throw new Error('Selected poster failed to decode')
				})),
				new Promise<never>((_, reject) => {
					abort = () => reject(new DOMException('Media selection aborted', 'AbortError'))
					if (signal.aborted) abort()
					else signal.addEventListener('abort', abort, { once: true })
				}),
			])
		} finally {
			signal.removeEventListener('abort', abort)
		}
	}

	private visibleMediaRegion(): number {
		const regions = [this.heroMedia, this.variantPannel]
		let visibleRegion = 0
		let visibleArea = 0
		for (const [index, region] of regions.entries()) {
			if (!region) continue
			const rect = region.getBoundingClientRect()
			const width = Math.max(0, Math.min(rect.right, innerWidth) - Math.max(rect.left, 0))
			const height = Math.max(0, Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0))
			const area = width * height
			if (area > visibleArea) {
				visibleArea = area
				visibleRegion = index
			}
		}
		return visibleRegion
	}

	private commitMediaRegion(
		regionIndex: number,
		region: HTMLElement,
		variantId: string,
		panelRequest: AbortController
	): boolean {
		if (
			this.panelRequest !== panelRequest ||
			panelRequest.signal.aborted ||
			!this.isConnected
		) return false
		this.captureMediaPauseIntent()
		region.dataset.selectedMediaVariant = variantId
		region.dataset.mediaState = 'selected'
		region.toggleAttribute('data-media-paused', this.mediaPaused)
		if (regionIndex === 0 && this.heroMedia) {
			this.heroMedia.replaceWith(region)
			this.heroMedia = region
			this.observeHeroCopy()
		} else if (regionIndex === 1 && this.variantPannel) {
			this.variantPannel.replaceWith(region)
			this.variantPannel = region
		} else return false
		if (this.transitionMotionEnabled()) {
			// Start from the loading wash (media at 0.68 over white, the look of
			// the 0.32 white overlay) instead of the dark wrapper background.
			region.setAttribute('data-media-revealing', '')
			const reveals = Array.from(
				region.querySelectorAll<HTMLElement>(
					':scope > .art-direction, .product-media__panel > .art-direction'
				),
				(media) => media.animate(
					[{ opacity: 0.68 }, { opacity: 1 }],
					{
						duration: this.mediaObscureDuration,
						easing: 'cubic-bezier(0.19, 1, 0.22, 1)',
					}
				).finished
			)
			void Promise.allSettled(reveals).then(() =>
				region.removeAttribute('data-media-revealing')
			)
		}
		return true
	}

	// Centred hero copy moves with the card and its own height, so the
	// mobile video controls read its bottom edge instead of a fixed offset.
	private observeHeroCopy(): void {
		if (!this.heroMedia || typeof ResizeObserver === 'undefined') return
		this.heroCopyObserver ??= new ResizeObserver(this.syncHeroCopyBottom)
		this.heroCopyObserver.disconnect()
		this.heroCopyObserver.observe(this.heroMedia)
		const card = this.closest<HTMLElement>('.product__info-wrapper')
		if (card) this.heroCopyObserver.observe(card)
		const copy = this.heroCopy()
		if (copy) this.heroCopyObserver.observe(copy)
	}

	private heroCopy(): HTMLElement | null {
		return (
			this.heroMedia?.querySelector<HTMLElement>(
				'.product-media__panel-content > :last-child'
			) ?? null
		)
	}

	private syncHeroCopyBottom = (): void => {
		const hero = this.heroMedia
		if (!hero) return
		const copy = this.heroCopy()
		if (!copy) {
			hero.style.removeProperty('--pdp-hero-copy-bottom')
			return
		}
		const offset =
			hero.getBoundingClientRect().bottom - copy.getBoundingClientRect().bottom
		hero.style.setProperty('--pdp-hero-copy-bottom', `${offset}px`)
	}

	private bindLoadingToggle(region: HTMLElement): void {
		if (region === this.heroMedia) this.observeHeroCopy()
		const button = region.querySelector<HTMLButtonElement>(
			'[data-pdp-media-loading-control] [data-uc-video-toggle]'
		)
		if (!button) return
		this.updateLoadingToggle(button)
		button.addEventListener('click', this.handleLoadingToggle)
	}

	private handleLoadingToggle = (event: Event): void => {
		event.preventDefault()
		this.mediaPaused = !this.mediaPaused
		for (const region of [this.heroMedia, this.variantPannel]) {
			if (!region) continue
			region.toggleAttribute('data-media-paused', this.mediaPaused)
			region
				.querySelectorAll<HTMLButtonElement>(
					'[data-pdp-media-loading-control] [data-uc-video-toggle]'
				)
				.forEach((button) => this.updateLoadingToggle(button))
		}
	}

	private updateLoadingToggle(button: HTMLButtonElement): void {
		const state = this.mediaPaused ? 'paused' : 'playing'
		const label = this.mediaPaused
			? button.dataset.labelPlay
			: button.dataset.labelPause
		button.dataset.state = state
		if (label) {
			button.setAttribute('aria-label', label)
			const labelElement = button.querySelector<HTMLElement>(
				'[data-uc-video-toggle-label]'
			)
			if (labelElement) labelElement.textContent = label
		}
	}

	private async renderVariantPanel(variantId: string): Promise<void> {
		this.variantPannel ??=
			this.closest('.product')?.querySelector('.product-media__panels') ??
			null
		const sectionId = this.closest<HTMLElement>('section')?.dataset.section
		if (!sectionId || !this.heroMedia || !this.variantPannel) return
		this.panelRequest?.abort()
		window.clearTimeout(this.panelTimeout)
		const panelRequest = new AbortController()
		this.panelRequest = panelRequest
		let timedOut = false
		const priorityRegion = this.visibleMediaRegion()
		this.captureMediaPauseIntent()
		for (const region of [this.heroMedia, this.variantPannel])
			region.dataset.mediaState = 'loading'
		this.panelTimeout = window.setTimeout(() => {
			timedOut = true
			panelRequest.abort()
		}, 8000)
		try {
			const url = new URL(window.location.href)
			url.searchParams.set('section_id', sectionId)
			url.searchParams.set('variant', variantId)
			const response = await fetch(url, { signal: panelRequest.signal })
			if (!response.ok)
				throw new Error(`Section render failed: ${response.status}`)
			const doc = new DOMParser().parseFromString(
				await response.text(),
				'text/html'
			)
			if (
				this.panelRequest !== panelRequest ||
				panelRequest.signal.aborted ||
				!this.isConnected
			)
				return
			const heroSource = doc.querySelector<HTMLElement>(
				'[data-product-hero-media]'
			)
			const panelSource = doc.querySelector<HTMLElement>('.product-media__panels')
			if (!heroSource || !panelSource)
				throw new Error('Section response is missing owned media')
			const newHeroMedia = document.importNode(heroSource, true)
			const newVariantPanel = document.importNode(panelSource, true)
			const subscriptionSource = doc.getElementById(
				`Subscription-${sectionId}`
			)
			const subscriptionDestination = document.getElementById(
				`Subscription-${sectionId}`
			)
			if (subscriptionSource && subscriptionDestination) {
				subscriptionDestination.innerHTML = subscriptionSource.innerHTML
				subscriptionDestination.toggleAttribute(
					'hidden',
					subscriptionSource.hasAttribute('hidden')
				)
				this.syncShadeDependentControls()
			}

			const regions = [newHeroMedia, newVariantPanel]
			const settleRegion = async (region: HTMLElement, index: number) => {
				try {
					await this.prepareRegionMedia(
						region,
						panelRequest.signal,
						index === priorityRegion ? 'high' : 'auto'
					)
					if (index === priorityRegion) this.clearPendingIndicator(variantId)
					await this.waitForMediaObscureFloor(panelRequest.signal)
					if (!this.commitMediaRegion(index, region, variantId, panelRequest)) return
				} catch {
					if (
						this.panelRequest !== panelRequest ||
						!this.isConnected ||
						(panelRequest.signal.aborted && !timedOut)
					) return
					await this.waitForMediaObscureFloor(panelRequest.signal).catch(() => undefined)
					this.showSelectedMediaFallback(variantId, index)
				} finally {
					if (index === priorityRegion) this.finishVariantTransition(variantId)
				}
			}
			await Promise.allSettled(
				regions.map((region, index) => settleRegion(region, index))
			)
		} catch {
			if (
				this.panelRequest !== panelRequest ||
				!this.isConnected ||
				(panelRequest.signal.aborted && !timedOut)
			) return
			for (const regionIndex of [0, 1])
				this.showSelectedMediaFallback(variantId, regionIndex)
			this.finishVariantTransition(variantId)
		} finally {
			if (this.panelRequest === panelRequest) {
				window.clearTimeout(this.panelTimeout)
				this.panelRequest = undefined
				this.finishVariantTransition(variantId)
			}
		}
	}

	private syncShadeDependentControls(): void {
		const inactive = this.requiresShade && !this.hasSelectedShade
		if (inactive) this.shadeLabel?.setAttribute('aria-disabled', 'true')
		else this.shadeLabel?.removeAttribute('aria-disabled')
		const subscription = this.querySelector<HTMLElement>(
			'[data-requires-product-selection]'
		)
		if (!subscription) return

		subscription.toggleAttribute('data-inactive', inactive)
		if (inactive) subscription.setAttribute('aria-disabled', 'true')
		else subscription.removeAttribute('aria-disabled')
		subscription
			.querySelectorAll<HTMLInputElement | HTMLButtonElement>(
				'input:not([type="hidden"]), button'
			)
			.forEach((control) => {
				control.disabled = inactive
			})
	}
}
